import { fetchWithSupabaseAuth } from "./supabaseClient.js";
import {
  DEPLOYMENT_UPDATE_BANNER_SETTING_KEY,
  publishDeploymentUpdateBannerEnabled,
} from "./deploymentUpdateConfig.js";
import {
  publishDispatchAlertCaptureStatus,
  publishReassignmentDiagnosticsStatus,
} from "./dispatchCaptureStatusEvents.js";

export const DISPATCH_ALERT_CAPTURE_CONFIG_ENDPOINT = "/api/system/diagnostics/alert-capture";
export const DISPATCH_ALERT_CAPTURE_TARGETS_ENDPOINT = `${DISPATCH_ALERT_CAPTURE_CONFIG_ENDPOINT}/targets`;
export const DISPATCH_ALERT_CAPTURES_ENDPOINT = "/api/system/diagnostics/alert-captures";
export const REASSIGNMENT_DIAGNOSTICS_HISTORY_ENDPOINT = "/api/system/diagnostics/reassignment-history";
export const REASSIGNMENT_DIAGNOSTICS_HISTORY_CONFIG_ENDPOINT = `${REASSIGNMENT_DIAGNOSTICS_HISTORY_ENDPOINT}/config`;
export const SOLLIO_UNIAG_ORDER_IMPORT_HISTORY_ENDPOINT = "/api/import/history/sollio-uniag-orders";
export const REPORT_ACTION_CAPTURE_HISTORY_ENDPOINT = "/api/exports/actions/history/captures";
export const SYSTEM_PERFORMANCE_REPORT_ENDPOINT = "/api/system/diagnostics/performance";
export const SYSTEM_PERFORMANCE_COLLECTION_ENDPOINT = `${SYSTEM_PERFORMANCE_REPORT_ENDPOINT}/collection`;
export const RAILWAY_SYSTEM_PERFORMANCE_ENDPOINT = `${SYSTEM_PERFORMANCE_REPORT_ENDPOINT}/railway`;
export const RAILWAY_SYSTEM_PERFORMANCE_AVAILABILITY_ENDPOINT = `${RAILWAY_SYSTEM_PERFORMANCE_ENDPOINT}/availability`;
export const RAILWAY_SYSTEM_PERFORMANCE_REPORT_ENDPOINT = `${RAILWAY_SYSTEM_PERFORMANCE_ENDPOINT}/report`;
export const RAILWAY_SYSTEM_PERFORMANCE_COLLECTION_ENDPOINT = `${RAILWAY_SYSTEM_PERFORMANCE_ENDPOINT}/collection`;

export class SystemConfigApiError extends Error {
  constructor(message, {
    payload = null,
    status = 0,
  } = {}) {
    super(message);
    this.name = "SystemConfigApiError";
    this.status = Number.isInteger(status) ? status : 0;
    this.payload = payload;
    this.code = typeof payload?.detail?.code === "string"
      ? payload.detail.code
      : typeof payload?.code === "string"
        ? payload.code
        : "";
  }
}

function resolveErrorMessage(payload, fallbackMessage) {
  if (payload && typeof payload.detail === "string" && payload.detail.trim()) {
    return payload.detail.trim();
  }

  if (payload?.detail && typeof payload.detail.message === "string" && payload.detail.message.trim()) {
    return payload.detail.message.trim();
  }

  if (payload && typeof payload.message === "string" && payload.message.trim()) {
    return payload.message.trim();
  }

  return fallbackMessage;
}

async function parseJsonResponse(response, fallbackMessage) {
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new SystemConfigApiError(resolveErrorMessage(payload, fallbackMessage), {
      payload,
      status: response.status,
    });
  }

  return payload;
}

async function requestJson(url, init, fallbackMessage) {
  const response = await fetchWithSupabaseAuth(url, init);
  return parseJsonResponse(response, fallbackMessage);
}

async function requestBlob(url, init, fallbackMessage) {
  const response = await fetchWithSupabaseAuth(url, init);
  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    throw new SystemConfigApiError(resolveErrorMessage(payload, fallbackMessage), {
      payload,
      status: response.status,
    });
  }

  return response.blob();
}

export async function fetchSystemConfigState() {
  return requestJson("/api/system-config", undefined, "Impossible de charger les paramètres système.");
}

export async function updateSystemConfigState(payload) {
  const response = await requestJson("/api/system-config", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, "Impossible d'enregistrer les paramètres système.");
  const bannerSetting = response?.state?.settings?.find(
    (setting) => setting.key === DEPLOYMENT_UPDATE_BANNER_SETTING_KEY,
  );
  publishDeploymentUpdateBannerEnabled(bannerSetting?.value);
  return response;
}

export async function toggleSystemDiagnostics(payload) {
  return requestJson("/api/system/diagnostics/toggle", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, "Impossible de mettre à jour le diagnostic performance.");
}

export async function createSystemDiagnosticsSnapshot(payload) {
  return requestJson("/api/system/diagnostics/snapshots", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, "Impossible d'enregistrer le snapshot diagnostic.");
}

export async function fetchSystemDiagnosticsSnapshots({ limit } = {}) {
  const searchParams = new URLSearchParams();
  if (Number.isInteger(limit) && limit >= 0) {
    searchParams.set("limit", String(limit));
  }

  const suffix = searchParams.toString() ? `?${searchParams.toString()}` : "";
  return requestJson(`/api/system/diagnostics/snapshots${suffix}`, undefined, "Impossible de charger l'historique diagnostics.");
}

export async function fetchSystemPerformanceReport({ fetchImpl } = {}) {
  return requestJson(SYSTEM_PERFORMANCE_REPORT_ENDPOINT, {
    cache: "no-store",
    fetchImpl,
    method: "GET",
  }, "Impossible de générer le rapport de performance.");
}

export async function updateSystemPerformanceCollection(payload, { fetchImpl } = {}) {
  return requestJson(SYSTEM_PERFORMANCE_COLLECTION_ENDPOINT, {
    body: JSON.stringify(payload),
    fetchImpl,
    headers: { "Content-Type": "application/json" },
    method: "PUT",
  }, "Impossible de mettre à jour la collecte de performance.");
}

export async function fetchRailwaySystemPerformanceAvailability({ fetchImpl } = {}) {
  return requestJson(RAILWAY_SYSTEM_PERFORMANCE_AVAILABILITY_ENDPOINT, {
    cache: "no-store",
    fetchImpl,
    method: "GET",
  }, "Impossible de vérifier le diagnostic Railway de secours.");
}

export async function fetchRailwaySystemPerformanceReport({ fetchImpl } = {}) {
  return requestJson(RAILWAY_SYSTEM_PERFORMANCE_REPORT_ENDPOINT, {
    cache: "no-store",
    fetchImpl,
    method: "POST",
  }, "Impossible de générer le rapport de performance Railway.");
}

export async function updateRailwaySystemPerformanceCollection(payload, { fetchImpl } = {}) {
  return requestJson(RAILWAY_SYSTEM_PERFORMANCE_COLLECTION_ENDPOINT, {
    body: JSON.stringify(payload),
    fetchImpl,
    headers: { "Content-Type": "application/json" },
    method: "PUT",
  }, "Impossible de mettre à jour la collecte de performance Railway.");
}

export async function fetchDispatchAlertCaptureConfig({ fetchImpl } = {}) {
  return requestJson(DISPATCH_ALERT_CAPTURE_CONFIG_ENDPOINT, {
    cache: "no-store",
    fetchImpl,
    method: "GET",
  }, "Impossible de charger la configuration des captures d’alertes.");
}

export async function updateDispatchAlertCaptureConfig(payload, { fetchImpl } = {}) {
  const nextConfig = await requestJson(DISPATCH_ALERT_CAPTURE_CONFIG_ENDPOINT, {
    body: JSON.stringify(payload),
    fetchImpl,
    headers: { "Content-Type": "application/json" },
    method: "PATCH",
  }, "Impossible de mettre à jour la collecte des alertes Dispatch.");
  publishDispatchAlertCaptureStatus(nextConfig?.is_active === true);
  return nextConfig;
}

export async function fetchDispatchAlertCaptureTargets({ fetchImpl } = {}) {
  return requestJson(DISPATCH_ALERT_CAPTURE_TARGETS_ENDPOINT, {
    cache: "no-store",
    fetchImpl,
    method: "GET",
  }, "Impossible de charger les utilisateurs du Dispatch.");
}

export function buildDispatchAlertCapturesQuery({
  alertType = "",
  campaignId = "",
  dispatchDate = "",
  page = 1,
  pageSize = 25,
  userId = "",
} = {}) {
  const searchParams = new URLSearchParams();
  searchParams.set("page", String(Number.isInteger(page) && page > 0 ? page : 1));
  searchParams.set("page_size", String(Number.isInteger(pageSize) && pageSize > 0 ? pageSize : 25));

  if (typeof campaignId === "string" && campaignId.trim()) {
    searchParams.set("campaign_id", campaignId.trim());
  }
  if (typeof userId === "string" && userId.trim()) {
    searchParams.set("user_id", userId.trim());
  }
  if (typeof dispatchDate === "string" && dispatchDate.trim()) {
    searchParams.set("dispatch_date", dispatchDate.trim());
  }
  if (typeof alertType === "string" && alertType.trim()) {
    searchParams.set("alert_type", alertType.trim());
  }

  return searchParams.toString();
}

export async function fetchDispatchAlertCaptures(filters = {}, { fetchImpl } = {}) {
  const query = buildDispatchAlertCapturesQuery(filters);
  return requestJson(`${DISPATCH_ALERT_CAPTURES_ENDPOINT}?${query}`, {
    cache: "no-store",
    fetchImpl,
    method: "GET",
  }, "Impossible de charger les captures des alertes Dispatch.");
}

export function buildSollioUniagOrderImportHistoryQuery({
  page = 1,
  pageSize = 25,
} = {}) {
  const searchParams = new URLSearchParams();
  searchParams.set("page", String(Number.isInteger(page) && page > 0 ? page : 1));
  searchParams.set("page_size", String(Number.isInteger(pageSize) && pageSize > 0 ? pageSize : 25));
  return searchParams.toString();
}

export async function fetchSollioUniagOrderImportHistory({
  page = 1,
  pageSize = 25,
} = {}, {
  fetchImpl,
} = {}) {
  const query = buildSollioUniagOrderImportHistoryQuery({ page, pageSize });
  return requestJson(`${SOLLIO_UNIAG_ORDER_IMPORT_HISTORY_ENDPOINT}?${query}`, {
    cache: "no-store",
    fetchImpl,
    method: "GET",
  }, "Impossible de charger l’historique des imports de commandes.");
}

export async function fetchReportActionCaptureHistory({
  page = 1,
  pageSize = 25,
} = {}, {
  fetchImpl,
} = {}) {
  const query = buildSollioUniagOrderImportHistoryQuery({ page, pageSize });
  return requestJson(`${REPORT_ACTION_CAPTURE_HISTORY_ENDPOINT}?${query}`, {
    cache: "no-store",
    fetchImpl,
    method: "GET",
  }, "Impossible de charger l’historique des rapports.");
}

export async function fetchDispatchAlertCaptureImage(captureId, {
  download = false,
  fetchImpl,
} = {}) {
  const normalizedCaptureId = typeof captureId === "string" ? captureId.trim() : "";
  if (!normalizedCaptureId) {
    throw new Error("Identifiant de capture manquant.");
  }

  const suffix = download ? "?download=true" : "";
  return requestBlob(
    `${DISPATCH_ALERT_CAPTURES_ENDPOINT}/${encodeURIComponent(normalizedCaptureId)}/image${suffix}`,
    {
      cache: "no-store",
      fetchImpl,
      method: "GET",
    },
    download
      ? "Impossible de télécharger cette capture."
      : "Impossible de charger l’image de cette capture.",
  );
}

export async function clearDispatchAlertCaptures({
  campaignId = "",
} = {}, {
  fetchImpl,
} = {}) {
  const searchParams = new URLSearchParams();
  if (typeof campaignId === "string" && campaignId.trim()) {
    searchParams.set("campaign_id", campaignId.trim());
  }
  const suffix = searchParams.toString() ? `?${searchParams.toString()}` : "";

  return requestJson(`${DISPATCH_ALERT_CAPTURES_ENDPOINT}${suffix}`, {
    fetchImpl,
    method: "DELETE",
  }, "Impossible d’effacer l’historique des captures.");
}

export async function fetchReassignmentDiagnosticsHistory({ fetchImpl, activityScope } = {}) {
  return requestJson(activityScope ? `${REASSIGNMENT_DIAGNOSTICS_HISTORY_ENDPOINT}?activity_scope=${encodeURIComponent(activityScope)}` : REASSIGNMENT_DIAGNOSTICS_HISTORY_ENDPOINT, {
    cache: "no-store",
    fetchImpl,
    method: "GET",
  }, "Impossible de charger l’historique de l’activité Dispatch.");
}

export async function fetchTruckOptimizationGanttCapture(captureId, { fetchImpl } = {}) {
  return requestJson(`${REASSIGNMENT_DIAGNOSTICS_HISTORY_ENDPOINT}/truck-gantt/${encodeURIComponent(captureId)}`, {
    cache: "no-store",
    fetchImpl,
    method: "GET",
  }, "Impossible de charger le Gantt de la capture.");
}

export async function fetchReassignmentDiagnosticsHistoryConfig({ fetchImpl } = {}) {
  return requestJson(REASSIGNMENT_DIAGNOSTICS_HISTORY_CONFIG_ENDPOINT, {
    cache: "no-store",
    fetchImpl,
    method: "GET",
  }, "Impossible de charger la configuration de l’activité Dispatch.");
}

export async function updateReassignmentDiagnosticsHistoryConfig(payload, { fetchImpl } = {}) {
  const nextConfig = await requestJson(REASSIGNMENT_DIAGNOSTICS_HISTORY_CONFIG_ENDPOINT, {
    body: JSON.stringify(payload),
    fetchImpl,
    headers: { "Content-Type": "application/json" },
    method: "PATCH",
  }, "Impossible de mettre à jour le cumul de l’activité Dispatch.");
  publishReassignmentDiagnosticsStatus(nextConfig?.is_active === true);
  return nextConfig;
}

export async function clearReassignmentDiagnosticsHistory({ fetchImpl, activityScope } = {}) {
  return requestJson(activityScope ? `${REASSIGNMENT_DIAGNOSTICS_HISTORY_ENDPOINT}?activity_scope=${encodeURIComponent(activityScope)}` : REASSIGNMENT_DIAGNOSTICS_HISTORY_ENDPOINT, {
    fetchImpl,
    method: "DELETE",
  }, "Impossible d’effacer l’historique de l’activité Dispatch.");
}
